import React, { useState, } from 'react';
import { ModeratorPermission, PermissionItem, MetricPermissionModel } from "../../../types/api/moderators";
import { permissionGroups, getSelectedPermissionsLabels } from "../ModerationList+Data";
import SectionSelector from '../../categories/components/select/generic/SectionSelector';
import { MetricPermissionsModal } from './MetricPermissionsModal';
import { MetricType, getMetricTitle } from '../../../types/api/metrics/metricsType';
import { Text, Stack } from '@primer/react';
import { ChevronRightIcon } from '@primer/octicons-react'

interface ModeratorPermissionsModalProps {
    permissions: Set<ModeratorPermission>;
    metricsPermissions: MetricPermissionModel;
    didPermissionsSelected: (list: Set<ModeratorPermission>, metric: MetricPermissionModel) => void;
}

const EMPTY_NUMBERS_ARRAY: number[] = [];
const EMPTY_STRINGS_ARRAY: string[] = [];
const EMPTY_SET = new Set<ModeratorPermission>();

export const ModeratorPermissionsModal: React.FC<ModeratorPermissionsModalProps> = ({ permissions, didPermissionsSelected }) => {

    const [selectedPermissions, setSelectedPermissions] = useState<Set<ModeratorPermission>>(EMPTY_SET);
    const [selectedMetricPermissions, setSelectedMetricPermissions] = useState<MetricPermissionModel>(
        Object.values(MetricType).reduce((acc, type) => ({
            ...acc,
            [type]: false
        }), {} as MetricPermissionModel)
    );

    const [metricModalOpened, setMetricModelOpened] = useState<boolean>(false);

    const handleMetricModalDismiss = () => {
        setMetricModelOpened(false);
    }

    const handlePermissionToggle = (permission: ModeratorPermission) => {
        const newSelected = new Set(selectedPermissions);
        if (newSelected.has(permission)) {
            newSelected.delete(permission);
        } else {
            newSelected.add(permission);
        }
        setSelectedPermissions(newSelected);
        didPermissionsSelected(newSelected, selectedMetricPermissions)
    };

    const handleGroupToggle = (groupItems: PermissionItem[]) => {
        const newSelected = new Set(selectedPermissions);
        const groupPermissions = groupItems.map(item => item.key);
        const allSelected = groupPermissions.every(permission => newSelected.has(permission));

        if (allSelected) {
            groupPermissions.forEach(permission => newSelected.delete(permission));
        } else {
            groupPermissions.forEach(permission => newSelected.add(permission));
        }

        setSelectedPermissions(newSelected);
        didPermissionsSelected(newSelected, selectedMetricPermissions)
    };

    const isGroupFullySelected = (groupItems: PermissionItem[]) => {
        return groupItems.every(item => selectedPermissions.has(item.key));
    };

    const isGroupPartiallySelected = (groupItems: PermissionItem[]) => {
        return groupItems.some(item => selectedPermissions.has(item.key)) && !isGroupFullySelected(groupItems);
    };

    const saveMetricPermissions = (metric: MetricPermissionModel) => {
        const setSpecificPermission = (permission: ModeratorPermission, isSelected: boolean) => {
            const newSelected = new Set(selectedPermissions);
            if (isSelected) {
                newSelected.add(permission);
            } else {
                newSelected.delete(permission);
            }

            setSelectedMetricPermissions(metric)
            setSelectedPermissions(newSelected);
            didPermissionsSelected(newSelected, metric)
        };

        const selectedMetrics = Object.values(metric).filter(value => value === true).length;
        setSpecificPermission(ModeratorPermission.CAN_VIEW_DASHBOARD_METRICS, selectedMetrics > 0)
    }

    const getSelectedLabels = (): string => {
        return getSelectedPermissionsLabels(permissionGroups, selectedPermissions)
    };

    const getSelectedMetricPermissionsLabels = (selectedMetricPermissions: MetricPermissionModel): string => {
        const labels: string[] = [];

        Object.entries(selectedMetricPermissions).forEach(([key, isSelected]) => {
            if (isSelected) {
                const title = getMetricTitle(key as MetricType);
                labels.push(title);
            }
        });

        return labels.join(', ');
    };

    const getTitle = () => {
        const selectedCount = selectedPermissions.size;
        const totalCount = new Set(permissionGroups.flatMap(group => group.items.map(item => item.key))).size;
        const selectedMetrics = Object.values(selectedMetricPermissions).filter(value => value === true).length;
        const isAllMetricSelected = selectedMetrics === Object.values(selectedMetricPermissions).length
        const selectedMetricsTitle = selectedMetrics === Object.values(selectedMetricPermissions).length ? ' (выбраны все метрики)' : ` (выбрано ${selectedMetrics} метрики)`
        const metricsTitle = selectedMetrics > 0 ? selectedMetricsTitle : ''

        const isSelectedAll = selectedCount === totalCount && isAllMetricSelected

        const labelForSelected = isSelectedAll ? 'Выбраны все' : getSelectedLabels()
        return selectedCount === 0 ? 'Выберите доступы' : labelForSelected + metricsTitle
    }

    const getMetricsTitle = () => {
        const totalCount = Object.values(MetricType).length;
        const selectedMetrics = Object.values(selectedMetricPermissions).filter(value => value === true).length;
        const isAllMetricSelected = selectedMetrics === Object.values(selectedMetricPermissions).length
        const selectedCount = Object.values(selectedMetricPermissions).filter(Boolean).length;

        const isSelectedAll = selectedCount === totalCount && isAllMetricSelected
        const labelForSelected = isSelectedAll ? 'Выбраны все' : getSelectedMetricPermissionsLabels(selectedMetricPermissions);

        return selectedCount === 0 ? 'Просмотр доступных метрик' : labelForSelected;
    }

    return (
        <SectionSelector
            triggerContent={
                <div className="flex flex-col min-w-0">
                    <span className={`text-[14px] ${permissions === undefined ? "text-aloix-mutated" : "text-aloix-black"} font-normal whitespace-nowrap`}>
                        {getTitle()}
                    </span>
                </div>
            }
            disabled={permissions === undefined}
            containerClassName="flex flex-col h-[444px]"
        >
            {(onClose) => (
                <div>
                    <div>
                        <Stack className="mt-3 h-[360px] block">
                            {metricModalOpened && <MetricPermissionsModal
                                permissions={selectedMetricPermissions}
                                didPermissionsSelected={saveMetricPermissions}
                                onDismiss={handleMetricModalDismiss}
                            />}
                            <div className="space-y-6 overflow-auto">
                                {permissionGroups.map((group, groupIndex) => (
                                    <div key={groupIndex} className="border border-grey">
                                        <Text
                                            fontSize={12}
                                            fontWeight="regular"
                                            color="fg.muted"
                                            className="ml-3"
                                            lineHeight="medium">
                                            {group.title}
                                        </Text>
                                        <div className="p-4 space-y-3">
                                            {group.items.map((item) => (
                                                <label key={item.key} className="flex items-center cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedPermissions.has(item.key)}
                                                        onChange={() => handlePermissionToggle(item.key)}
                                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                                    />
                                                    <Text
                                                        fontSize={14}
                                                        fontWeight="medium"
                                                        color="fg.default"
                                                        className="ml-3"
                                                        lineHeight="medium">
                                                        {item.label}
                                                    </Text>
                                                </label>
                                            ))}
                                            {group.title === 'Дашборд по метрикам' && (
                                                <button
                                                    onClick={() => setMetricModelOpened(true)}
                                                    className='w-full h-[40px] p-0 bg-aloix-disabled border-[#818B981] border rounded-lg mx-auto flex items-center justify-center px-4'
                                                >
                                                    <Stack direction="horizontal" align="center" className='w-full'>
                                                        <div className="flex items-center w-full gap-4 overflow-hidden">
                                                            <div className="flex flex-col min-w-0">
                                                                <span className={`text-[14px] ${permissions === undefined ? "text-aloix-mutated" : "text-aloix-black"} font-normal whitespace-nowrap`}>
                                                                    {getMetricsTitle()}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <ChevronRightIcon />
                                                    </Stack>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Stack>
                    </div>
                </div>
            )}

        </SectionSelector>
    );
};